import { forwardRef, FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  NotificationData,
  MessageData,
  MSG_EVENT
} from '@DStypes/notifications';
// @mui material components
import MenuItem from '@mui/material/MenuItem';
import Link from '@mui/material/Link';
import { MenuItemProps, Tooltip } from '@mui/material';
import dayjs from 'dayjs';

// Material Dashboard 2 PRO React TS components
import { MDBox, MDTypography } from '@components/index';
import {
  generateLink,
  getNotificationMessageWithLink
} from '@utils/notifications';

// custom styles for the NotificationItem
import menuItem from './styles';
import { useUpdateNotificationAsReadMutation } from '@api/notifications';
import { RootState } from '@store/store';

import './styles.scss';
import { markUserMessageAsRead } from '@components/Notification/slice';
import useCustomNavigate from '@hooks/useCustomNavigate';
import useDownloadReport from '@hooks/useDownloadReport';

interface Props extends MenuItemProps {
  messageObj: NotificationData;
  handleAutoSaveTimeChange: () => void;
}

const NotificationItem: FC<Props> = forwardRef((props, ref) => {
  const { messageObj, handleAutoSaveTimeChange, ...rest } = props;

  const {
    message_type: type = 'secondary',
    created_at: createdAt,
    message_data,
    id: message_id,
    is_read,
    message_event,
    is_user_message
  } = messageObj;

  const messageData: MessageData = JSON.parse(message_data);

  const [markAsRead] = useUpdateNotificationAsReadMutation();
  const userId = useSelector(
    (state: RootState) => state?.userDetails?.user?.id
  );
  const notificationsData = useSelector(
    (state: RootState) => state?.notificationsData
  );

  const { downloadReport, downloadCleanedFile } = useDownloadReport();

  const [autoSaveTime, setAutoSaveTime] = useState<string>('---');
  const navigate = useCustomNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (createdAt) {
      const interval = setInterval(() => {
        setAutoSaveTime(dayjs(createdAt).fromNow());
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [createdAt]);

  useEffect(() => {
    handleAutoSaveTimeChange();
  }, [autoSaveTime]);

  const handleNotificationAction = async () => {
    if (!is_read)
      if (!is_user_message)
        await markAsRead({ id: userId, message_id: [message_id as string] });
      else dispatch(markUserMessageAsRead({ notificationsData, message_id }));

    if (!isLink) navigate(generateLink(messageObj) as string);
  };

  const isLinkMessages = [
    MSG_EVENT.FILE_UPLOAD_FAIL,
    MSG_EVENT.DATA_ANONYMIZED
  ];

  const isLink = isLinkMessages.includes(message_event);

  return (
    <MenuItem
      {...rest}
      ref={ref}
      sx={(theme) => ({
        ...menuItem(theme),
        maxWidth: '100%',
        paddingTop: '0px'
      })}
      onClick={handleNotificationAction}
    >
      {isLink ? (
        <Link href={generateLink(messageObj)} target='_blank'>
          <MDBox display='block' width='100%' sx={{ border: '0px solid blue' }}>
            <MDBox display='flex'>
              {!is_read && (
                <MDBox>
                  <MDBox
                    bgColor={type.toLowerCase()}
                    sx={{ height: 10, width: 10, borderRadius: '100%', mt: 1 }}
                  />
                </MDBox>
              )}
              <MDBox
                component={Link}
                py={0.5}
                flexWrap='wrap'
                alignItems='center'
                lineHeight={1}
                maxWidth='100%'
              >
                <MDTypography
                  variant='button'
                  fontWeight='normal'
                  whiteSpace='pre-wrap'
                  sx={{
                    overflowWrap: 'anywhere',
                    fontSize: '.75rem',
                    ml: is_read ? 0 : 1
                  }}
                >
                  {getNotificationMessageWithLink(messageObj, navigate, true)}
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDBox
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              sx={{ borderBottom: `0.2px solid #67748e4d` }}
              py={1}
            >
              <Tooltip title={messageData?.project?.name || ''}>
                <MDTypography
                  variant='xs'
                  fontWeight='bold'
                  whiteSpace='pre-wrap'
                  className='line-clamp-three'
                  sx={{
                    overflowWrap: 'anywhere',
                    paddingRight: '2rem'
                  }}
                >
                  {messageData?.project?.name}
                </MDTypography>
              </Tooltip>
              <MDTypography
                variant='h6'
                color='info'
                fontWeight='bold'
                sx={{ textAlign: 'end' }}
              >
                {autoSaveTime}
              </MDTypography>
            </MDBox>
          </MDBox>
        </Link>
      ) : (
        <MDBox display='block' width='100%' sx={{ border: '0px solid blue' }}>
          <MDBox display='flex'>
            {!is_read && (
              <MDBox>
                <MDBox
                  bgColor={type.toLowerCase()}
                  sx={{ height: 10, width: 10, borderRadius: '100%', mt: 1 }}
                />
              </MDBox>
            )}
            <MDBox
              component={Link}
              py={0.5}
              flexWrap='wrap'
              alignItems='center'
              lineHeight={1}
              maxWidth='100%'
            >
              <MDTypography
                variant='button'
                fontWeight='normal'
                whiteSpace='pre-wrap'
                sx={{
                  overflowWrap: 'anywhere',
                  fontSize: '.75rem',
                  ml: is_read ? 0 : 1
                }}
              >
                {messageObj.message_event ===
                MSG_EVENT.REPORT_GENERATION_COMPLETED
                  ? getNotificationMessageWithLink(
                      messageObj,
                      navigate,
                      true,
                      (id: string, name: string) => {
                        downloadReport(id, name);
                      }
                    )
                  : MSG_EVENT.CLEANED_FILE_GENERATION_COMPLETED
                  ? getNotificationMessageWithLink(
                      messageObj,
                      navigate,
                      true,
                      (id: string, name: string) => {
                        downloadCleanedFile(id, name);
                      }
                    )
                  : getNotificationMessageWithLink(messageObj, navigate, true)}
              </MDTypography>
            </MDBox>
          </MDBox>

          <MDBox
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            sx={{ borderBottom: `0.2px solid #67748e4d` }}
            py={1}
          >
            <Tooltip title={messageData?.project?.name || ''}>
              <MDTypography
                variant='xs'
                fontWeight='bold'
                whiteSpace='pre-wrap'
                className='line-clamp-three'
                sx={{
                  overflowWrap: 'anywhere',
                  paddingRight: '2rem'
                }}
              >
                {messageData?.project?.name}
              </MDTypography>
            </Tooltip>
            <MDTypography
              variant='h6'
              color='info'
              fontWeight='bold'
              sx={{ textAlign: 'end' }}
            >
              {autoSaveTime}
            </MDTypography>
          </MDBox>
        </MDBox>
      )}
    </MenuItem>
  );
});

export default NotificationItem;
